import { BookOpen } from 'lucide-react';
import { Spinner } from '@/components/ui';
import { useLedgerTransactions } from '@/hooks/use-ledger';
import { useLoan } from '@/hooks/use-loans';
import type { Transaction } from '@/types';

function fmt(value: string): string {
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
  }).format(num);
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-AU', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const STATUS_COLORS: Record<string, string> = {
  APPLIED: 'bg-green-50 text-green-700',
  VOID: 'bg-red-50 text-red-700',
  QUEUED: 'bg-amber-50 text-amber-700',
};

interface Props {
  loanId: string;
}

export function LedgerTab({ loanId }: Props) {
  const { data: loan, isLoading: loadingLoan } = useLoan(loanId);
  const ledgerId = loan?.ledgerId;
  const { data: transactions, isLoading: loadingTxns } = useLedgerTransactions(ledgerId);

  const isLoading = loadingLoan || loadingTxns;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400">
        <Spinner size="lg" className="w-5 h-5 animate-spin mr-2" />
        Loading ledger…
      </div>
    );
  }

  // Balances summary
  const balances = loan?.ledger?.balances ?? [];

  return (
    <div className="space-y-6">
      {/* Balance cards */}
      {balances.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {balances.map(b => (
            <div key={b.id} className="bg-white rounded-lg border border-gray-200 p-4">
              <p className="text-xs text-gray-500 capitalize mb-1">{b.indicator}</p>
              <p className="text-xl font-semibold text-gray-900">{fmt(b.balance)}</p>
              <div className="flex gap-3 mt-2 text-[11px] text-gray-400">
                <span>Credits: {fmt(b.creditBalance)}</span>
                <span>Debits: {fmt(b.debitBalance)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Transactions table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-900">Transactions</h3>
        </div>

        {!transactions || transactions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <BookOpen className="w-8 h-8 mb-2" />
            <p className="text-sm">No transactions recorded</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Date</th>
                <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">
                  Description
                </th>
                <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">From</th>
                <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">To</th>
                <th className="text-right text-xs font-medium text-gray-500 px-4 py-3">Amount</th>
                <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {transactions.map((txn: Transaction) => {
                const statusStyle = STATUS_COLORS[txn.status] ?? STATUS_COLORS.QUEUED;
                return (
                  <tr key={txn.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-2.5 text-sm text-gray-500 whitespace-nowrap">
                      {fmtDate(txn.createdAt)}
                    </td>
                    <td className="px-4 py-2.5 text-sm text-gray-900">
                      {txn.description || txn.reference || '—'}
                    </td>
                    <td className="px-4 py-2.5 text-sm text-gray-500 capitalize">
                      {txn.sourceBalance?.indicator ?? '—'}
                    </td>
                    <td className="px-4 py-2.5 text-sm text-gray-500 capitalize">
                      {txn.destinationBalance?.indicator ?? '—'}
                    </td>
                    <td className="px-4 py-2.5 text-sm font-medium text-gray-900 text-right">
                      {fmt(txn.amount)}
                    </td>
                    <td className="px-4 py-2.5">
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusStyle}`}
                      >
                        {txn.status}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
